/**
 * TanStack Query hooks for `campaign-states`. Thin wrappers over ./api.
 *
 * Exposes:
 *   useCampaignStatesHistory(id)   — timeline + current state
 *   useCampaignStatesAllowed(id)   — list of transition buttons to render
 *   useCampaignStatesTransition()  — POST /transition mutation
 *
 * Legacy list/create/delete hooks are kept as thin stubs so the scaffolded
 * test and any import surface continue to type-check.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	getAllowed,
	getHistory,
	list,
	remove,
	transition,
	campaignStatesQueryKeys,
	type AllowedResponse,
	type CampaignRow,
	type CampaignStatesRow,
	type CreateCampaignStatesBody,
	type HistoryResponse,
	type ListCampaignStatesResponse,
	type TransitionBody,
	type TransitionResponse,
} from "./api";

export function useCampaignStatesList(): UseQueryResult<
	ListCampaignStatesResponse,
	Error
> {
	return useQuery({
		queryKey: campaignStatesQueryKeys.list(),
		queryFn: list,
	});
}

export function useCampaignStatesHistory(
	campaignId: string,
): UseQueryResult<HistoryResponse, Error> {
	return useQuery({
		queryKey: campaignStatesQueryKeys.history(campaignId),
		queryFn: () => getHistory(campaignId),
		enabled: campaignId.length > 0,
	});
}

export function useCampaignStatesAllowed(
	campaignId: string,
): UseQueryResult<AllowedResponse, Error> {
	return useQuery({
		queryKey: campaignStatesQueryKeys.allowed(campaignId),
		queryFn: () => getAllowed(campaignId),
		enabled: campaignId.length > 0,
	});
}

export function useCampaignStatesTransition(): UseMutationResult<
	TransitionResponse,
	Error,
	TransitionBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: transition,
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({
				queryKey: campaignStatesQueryKeys.history(vars.campaign_id),
			});
			qc.invalidateQueries({
				queryKey: campaignStatesQueryKeys.allowed(vars.campaign_id),
			});
		},
	});
}

// Legacy scaffold hooks retained for scaffold-test compatibility.

export function useCampaignStatesCreate(): UseMutationResult<
	CampaignRow,
	Error,
	CreateCampaignStatesBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignStatesQueryKeys.all });
		},
	});
}

export function useCampaignStatesDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignStatesQueryKeys.all });
		},
	});
}

export type { CampaignStatesRow };
